/*
 * Copyright (c) CIRROM 2025.
 */

import React from "react";
import { PageTitle } from "../utils/PageTitle";
import { useSystemOrProject } from "../integrated-systems/hooks";
import { IntegratedSystem } from "../../api/integratedsystems";
import { DataProject } from "../../api/dataprojects";
import { useParams } from "react-router-dom";
import classes from "./TempReportStyles.module.css";
import Typography from "@mui/material/Typography";
export const TempReport = (props: {
  userSystems: IntegratedSystem[] | DataProject[];
  setSelectedSystemId: (number) => void;
}) => {
  const { integratedSystemId } = useParams();
  const systemOrProject = useSystemOrProject(
    props.userSystems,
    integratedSystemId
  );

  return (
    <PageTitle
      pageTitle="Contact Monitor | CIRROM"
      pageHeader="Contact Match Analysis"
      isDataProject
      integratedSystemId={Number.parseInt(integratedSystemId)}
      systemOrProjectHeader={systemOrProject?.name}
    >
      <div className={classes.dashboardContainer}>
        <div className={classes.card}>
          <div className={classes.cardContent}>
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <Typography variant={"h4"} fontWeight={"bold"} color={"grey"}>
                Total Contacts Analyzed
              </Typography>
              <div
                style={{
                  color: "#d32f2f",
                }}
              >
                <Typography variant={"h2"} fontWeight={"bolder"}>
                  118
                </Typography>
              </div>
            </div>

            <div className={classes.statsGrid}>
              <div className={`${classes.statCard} ${classes.exact}`}>
                <div className={classes.number}>
                  <Typography variant={"h3"} fontWeight={"bolder"}>
                    57
                  </Typography>
                </div>
                <div className={classes.percentage}>
                  <Typography variant={"h5"}>48%</Typography>
                </div>

                <Typography variant={"caption"}>
                  Exact/Enriched Matches: LI data matches CRM or can enrich
                  current CRM data
                </Typography>
              </div>

              <div className={`${classes.statCard} ${classes.partial}`}>
                <div className={classes.number}>
                  <Typography variant={"h3"} fontWeight={"bolder"}>
                    40
                  </Typography>
                </div>
                <div className={classes.percentage}>
                  <Typography variant={"h5"}>34%</Typography>
                </div>
                <Typography variant={"caption"}>
                  Partial Matches: Some data scraped from LI matches CRM
                </Typography>
              </div>

              <div className={`${classes.statCard} ${classes.noProfile}`}>
                <div className={classes.number}>
                  <Typography variant={"h3"} fontWeight={"bolder"}>
                    14
                  </Typography>
                </div>
                <div className={classes.percentage}>
                  <Typography variant={"h5"}> 12% </Typography>
                </div>
                <Typography variant={"caption"}>
                  No LinkedIn Profile: Contacts without public, searchable LI
                  profile
                </Typography>
              </div>

              <div className={`${classes.statCard} ${classes.noMatch}`}>
                <div className={classes.number}>
                  <Typography variant={"h3"} fontWeight={"bolder"}>
                    10
                  </Typography>
                </div>
                <div className={classes.percentage}>
                  <Typography variant={"h5"}>8%</Typography>
                </div>
                <Typography variant={"caption"}>
                  No Cirrom Match: Contacts with LI profile but Cirrom couldn't
                  match
                </Typography>
              </div>
            </div>

            <div
              style={{
                marginTop: "30px",
                marginBottom: "15px",
                fontWeight: "600",
                fontSize: "16px",
              }}
            >
              <Typography variant={"h5"} fontWeight={"bold"}>
                Data Discrepancy Analysis
              </Typography>
            </div>
            <div className={`${classes.statsGrid}`}>
              <div className={`${classes.statCard} ${classes.jobChange}`}>
                <div className={classes.number}>
                  <Typography variant={"h3"} fontWeight={"bolder"}>
                    25
                  </Typography>
                </div>
                <div className={classes.percentage}>
                  <Typography variant={"h5"}>21%</Typography>
                </div>
                <Typography variant={"caption"}>
                  Job Title Change: CRM job title and LinkedIn job title are not
                  exact match
                </Typography>
              </div>

              <div className={`${classes.statCard} ${classes.jobTitle}`}>
                <div className={classes.number}>
                  <Typography variant={"h3"} fontWeight={"bolder"}>
                    25
                  </Typography>
                </div>
                <div className={classes.percentage}>
                  <Typography variant={"h5"}> 21% </Typography>
                </div>
                <Typography variant={"caption"}>
                  Enriched Job Title: Cirrom filled a blank job title field from
                  matched LI profile
                </Typography>
              </div>

              <div className={`${classes.statCard} ${classes.jobMovers}`}>
                <div className={classes.number}>
                  <Typography variant={"h3"} fontWeight={"bolder"}>
                    10
                  </Typography>
                </div>
                <div className={classes.percentage}>
                  <Typography variant={"h5"}> 8% </Typography>
                </div>
                <Typography variant={"caption"}>
                  Job Movers: CRM company and LinkedIn company are different
                </Typography>
              </div>

              <div className={`${classes.statCard} ${classes.nameChanges}`}>
                <div className={classes.number}>
                  <Typography variant={"h3"} fontWeight={"bolder"}>
                    11
                  </Typography>
                </div>
                <div className={classes.percentage}>
                  <Typography variant={"h5"}> 9% </Typography>
                </div>
                <Typography variant={"caption"}>
                  Name Changes: CRM name and LinkedIn name are not exact match
                </Typography>
              </div>

              <div className={`${classes.statCard} ${classes.accreditations}`}>
                <div className={classes.number}>
                  <Typography variant={"h3"} fontWeight={"bolder"}>
                    6
                  </Typography>
                </div>
                <div className={classes.percentage}>
                  <Typography variant={"h5"}> 5% </Typography>
                </div>
                <Typography variant={"caption"}>
                  Enriched Accreditations: Cirrom filled a blank
                  accreditation/suffix
                </Typography>
              </div>
            </div>

            <div className={classes.vizContainer}>
              <div className={classes.vizRow}>
                <div className={classes.vizSectionTitle}>
                  <Typography variant={"h5"} fontWeight={"bold"} color={"grey"}>
                    Match Type Distribution
                  </Typography>
                </div>
                <div className={classes.pieChart}>
                  <div className={classes.pieCenter}>118 Total</div>
                </div>
                <div className={classes.legend}>
                  <div className={classes.legendItem}>
                    <div
                      className={classes.legendColor}
                      style={{ backgroundColor: "#8BC34A" }}
                    ></div>
                    <Typography variant={"caption"}>
                      Exact/Enriched Matches
                    </Typography>
                    <div className={classes.legendPercentage}>
                      <Typography variant={"caption"}>48%</Typography>
                    </div>
                  </div>
                  <div className={classes.legendItem}>
                    <div
                      className={classes.legendColor}
                      style={{ backgroundColor: "#FFC107" }}
                    ></div>
                    <Typography variant={"caption"}>Partial Matches</Typography>
                    <div className={classes.legendPercentage}>
                      <Typography variant={"caption"}>34%</Typography>
                    </div>
                  </div>
                  <div className={classes.legendItem}>
                    <div
                      className={classes.legendColor}
                      style={{ backgroundColor: "#FF9800" }}
                    ></div>
                    <Typography variant={"caption"}>
                      No LinkedIn Profile
                    </Typography>
                    <div className={classes.legendPercentage}>
                      <Typography variant={"caption"}>12%</Typography>
                    </div>
                  </div>
                  <div className={classes.legendItem}>
                    <div
                      className={classes.legendColor}
                      style={{ backgroundColor: "#F44336" }}
                    ></div>
                    <Typography variant={"caption"}>No Cirrom Match</Typography>
                    <div className={classes.legendPercentage}>
                      <Typography variant={"caption"}>8%</Typography>
                    </div>
                  </div>
                </div>
              </div>

              <div className={classes.vizRow}>
                <div className={classes.vizSectionTitle}>
                  <Typography variant={"h5"} fontWeight={"bold"} color={"grey"}>
                    Data Discrepancies
                  </Typography>
                </div>
                <div className={classes.barChart}>
                  <div className={classes.barItem}>
                    <div className={classes.barLabel}>
                      <Typography variant={"caption"}>
                        Job Title Change
                      </Typography>
                      <span>
                        <Typography variant={"caption"}>21%</Typography>
                      </span>
                    </div>
                    <div className={classes.barTrack}>
                      <div
                        className={classes.barFill}
                        style={{ width: "21%", backgroundColor: "#2196F3" }}
                      ></div>
                    </div>
                  </div>

                  <div className={classes.barItem}>
                    <div className={classes.barLabel}>
                      <Typography variant={"caption"}>
                        Enriched Job Title
                      </Typography>
                      <span>
                        <Typography variant={"caption"}>21%</Typography>
                      </span>
                    </div>
                    <div className={classes.barTrack}>
                      <div
                        className={classes.barFill}
                        style={{ width: "21%", backgroundColor: "#4CAF50" }}
                      ></div>
                    </div>
                  </div>

                  <div className={classes.barItem}>
                    <div className={classes.barLabel}>
                      <Typography variant={"caption"}>Name Changes</Typography>
                      <span>
                        <Typography variant={"caption"}>9%</Typography>
                      </span>
                    </div>
                    <div className={classes.barTrack}>
                      <div
                        className={classes.barFill}
                        style={{ width: "9%", backgroundColor: "#795548" }}
                      ></div>
                    </div>
                  </div>

                  <div className={classes.barItem}>
                    <div className={classes.barLabel}>
                      <Typography variant={"caption"}> Job Movers</Typography>
                      <span>
                        <Typography variant={"caption"}>8%</Typography>
                      </span>
                    </div>
                    <div className={classes.barTrack}>
                      <div
                        className={classes.barFill}
                        style={{ width: "8%", backgroundColor: "#9C27B0" }}
                      ></div>
                    </div>
                  </div>

                  <div className={classes.barItem}>
                    <div className={classes.barLabel}>
                      <Typography variant={"caption"}>
                        Enriched Accreditations
                      </Typography>
                      <span>
                        <Typography variant={"caption"}>5%</Typography>
                      </span>
                    </div>
                    <div className={classes.barTrack}>
                      <div
                        className={classes.barFill}
                        style={{ width: "5%", backgroundColor: "#009688" }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageTitle>
  );
};
